<template>
  <div class="measurement-dialog">
    <a-row class="measurement-content" type="flex" justify="space-between">
      <a-col :lg="8" :xs="25" class="camera-pd">
        <p>PD measurement with camera</p>
        <a-button @click="handleOpenCameraPDDialog" class="camera-pd-btn" block size="large" shape="circle" icon="camera" />
      </a-col>
      <a-col :lg="9" :xs="25" class="center-divider">
        or
      </a-col>
      <a-col :lg="8" :xs="25" class="manual-pd">
        <p>Enter your PD:</p>
        <a-form layout="inline" :form="form">
          <a-form-item>
            <a-input style="border-radius: 20px;" size="large" placeholder="Your PD" v-model="pd" @change="updatePD" />
          </a-form-item>
          <a-form-item>
            <a-button size="large" class="pd-submit-btn" html-type="submit" @click="handleClose">
              Submit
            </a-button>
          </a-form-item>
        </a-form>
      </a-col>
    </a-row>

    <a-modal
      v-model="showCameraPDDialog"
      class="pd-modal"
      width="768px"
      height="auto"
      :mask-closable="false"
      :closable="false"
      :footer="null"
      :destroy-on-close="true"
      :body-style="{ padding: 0 }"
    >
      <div class="pd-modal--container">
        <div class="pd-modal--title">
          <span>Measure your PD</span>
          <div class="close-icon" @click="handleCloseCameraPDDialog">
            <close-icon :style="{ fontSize: '24px', cursor: 'pointer' }" />
          </div>
        </div>
        <div class="pd-modal--content">
          <p v-if="!reviewMode">Position your face and the card within the outlines.</p>
          <p v-if="reviewMode">
            Move the eye icons to the center of your eyes and adjust the card corner exactly.
          </p>
          <div class="pd-measure-view" ref="container">
            <video v-if="!reviewMode" class="pd-webcam" ref="webcam" />
            <img v-if="!reviewMode" class="pd-mask" :src="require('../../assets/images/pd_mask.png')" />
            <img v-if="reviewMode" class="pd-result" draggable="false" :src="screenshot" />
            <span v-if="reviewMode" class="pd-value" v-html="'Your PD: ' + localPD + 'mm'"></span>
            <img
              v-if="reviewMode"
              class="pd-anchor"
              draggable="false"
              ref="cardL"
              :src="require('../../assets/images/ic_card_left.png')"
              :style="{ top: '30%', left: '35%' }"
              @mousedown="handleMouseDown"
              @mousemove="handleMouseMove"
              @mouseout="handleMouseEnd"
              @mouseup="handleMouseEnd"
            />
            <img
              v-if="reviewMode"
              class="pd-anchor"
              draggable="false"
              ref="cardR"
              :src="require('../../assets/images/ic_card_right.png')"
              :style="{ top: '30%', left: '65%' }"
              @mousedown="handleMouseDown"
              @mousemove="handleMouseMove"
              @mouseout="handleMouseEnd"
              @mouseup="handleMouseEnd"
            />
            <img
              v-if="reviewMode"
              class="pd-anchor"
              draggable="false"
              ref="eyeL"
              :src="require('../../assets/images/ic_eye.png')"
              :style="{ top: '45%', left: '40%' }"
              @mousedown="handleMouseDown"
              @mousemove="handleMouseMove"
              @mouseout="handleMouseEnd"
              @mouseup="handleMouseEnd"
            />
            <img
              v-if="reviewMode"
              class="pd-anchor"
              draggable="false"
              ref="eyeR"
              :src="require('../../assets/images/ic_eye.png')"
              :style="{ top: '45%', left: '60%' }"
              @mousedown="handleMouseDown"
              @mousemove="handleMouseMove"
              @mouseout="handleMouseEnd"
              @mouseup="handleMouseEnd"
            />
          </div>
        </div>
        <div class="pd-modal--btn-group">
          <a-button v-if="!reviewMode" @click="handleTakePicture" size="large" class="btn">
            <a-icon style="font-size: 24px;" type="camera" />
          </a-button>
          <a-button v-if="reviewMode" @click="handleRetake" size="large" class="btn btn__outline">
            Retake
          </a-button>
          <a-button v-if="reviewMode" @click="handleConfirm" size="large" class="btn">
            Confirm
          </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { Modal } from "ant-design-vue";
import { CloseIcon } from "../Icons";

export default {
  data() {
    return {
      showCameraPDDialog: false,
      reviewMode: false,
      screenshot: null,
      stream: null,
      canvas: null,
      ctx: null,
      mouseTarget: null,
      localPD: 0
    };
  },
  components: {
    CloseIcon
  },
  created() {
    this.form = this.$form.createForm(this);
  },
  computed: {
    pd() {
      return this.$store.state.pd;
    }
  },
  methods: {
    handleClose() {
      this.$emit("close");
    },
    handleConfirm() {
      this.$store.commit("updatePD", this.localPD);
      this.handleCloseCameraPDDialog();
    },
    handleOpenCameraPDDialog() {
      this.showCameraPDDialog = true;
      this.openCamera();
    },
    handleCloseCameraPDDialog() {
      this.pauseCamera();
      this.reviewMode = false;
      this.showCameraPDDialog = false;
    },
    handleTakePicture() {
      this.screenshot = this.capture();
      this.pauseCamera();
      this.reviewMode = true;
      requestAnimationFrame(() => this.calcPD());
    },
    handleRetake() {
      this.reviewMode = false;
      this.openCamera();
    },
    updatePD(e) {
      this.$store.commit("updatePD", e.target.value);
    },
    openCamera() {
      navigator.getUserMedia =
        navigator.getUserMedia ||
        navigator.webkitGetUserMedia ||
        navigator.mozGetUserMedia ||
        navigator.msGetUserMedia ||
        navigator.oGetUserMedia;
      // New api for video and audio called navigator.mediadevices
      // for new browsers video can directly consume MediaStream using srcObject
      if (navigator.mediaDevices?.getUserMedia) {
        navigator.mediaDevices
          .getUserMedia({ audio: false, video: { facingMode: "user", width: 640, height: 480 } })
          .then(stream => {
            this.stream = stream;
            this.$refs.webcam.srcObject = stream;
            this.$refs.webcam.onloadedmetadata = () => {
              this.$refs.webcam.play();
              // Update UI ratio based on camera dimension
              this.$refs.container.style.height = (this.$refs.container.clientWidth * 480) / 640 + "px";
            };
          })
          .catch(err => Modal.error({ title: "Exception", content: err.message }));
      }
    },
    pauseCamera() {
      this.$refs.webcam?.pause();
      this.stream?.getTracks()?.[0]?.stop();
      this.stream = null;
    },
    capture() {
      const video = this.$refs.webcam;
      if (!this.ctx) {
        const canvas = document.createElement("canvas");
        canvas.width = video.clientWidth;
        canvas.height = video.clientHeight;
        this.canvas = canvas;
        this.ctx = canvas.getContext("2d");
      }
      this.ctx.drawImage(video, 0, 0, this.canvas.width, this.canvas.height);
      return this.canvas.toDataURL("image/png");
    },
    handleMouseDown(e) {
      this.mouseTarget = e.target;
    },
    handleMouseMove(e) {
      const containerRect = this.$refs.container.getBoundingClientRect();
      [this.$refs.cardL, this.$refs.cardR, this.$refs.eyeL, this.$refs.eyeR].forEach(item => {
        if (item === this.mouseTarget) {
          item.style.left = e.clientX - containerRect.left + "px";
          item.style.top = e.clientY - containerRect.top + "px";
          this.calcPD();
        }
      });
    },
    handleMouseEnd() {
      this.mouseTarget = null;
    },
    calcPD() {
      const pd =
        (85.6 * (this.$refs.eyeR.offsetLeft - this.$refs.eyeL.offsetLeft)) /
        (this.$refs.cardR.offsetLeft - this.$refs.cardL.offsetLeft);
      this.localPD = Number(pd.toFixed(2));
    }
  }
};
</script>

<style lang="scss" scoped>
.pd-modal {
  &--title {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 40px 0 24px;

    span {
      color: #193e70;
      font-size: 20px;
      font-weight: 600;
    }
  }

  &--container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  &--btn-group {
    margin-top: 24px;
    width: 40%;
    padding-bottom: 32px;
    display: flex;
    justify-content: space-around;
    align-items: center;

    .btn {
      border: 2px solid #f9d802;
      border-radius: 25px;
      height: 50px;
      width: 120px;
      background: #f9d802;

      &__outline {
        background: #fff;
      }

      &:hover,
      &:focus {
        color: rgba(0, 0, 0, 0.65);
      }
    }

    @media only screen and (max-width: 768px) {
      width: 80%;
    }
  }

  &--content {
    padding: 0 10%;
    width: 100%;
    margin-top: 24px;

    p {
      color: #333333;
      font-size: 16px;
    }

    .pd-measure-view {
      height: 500px;
      // border: 6px solid #f9d802;
      border-radius: 16px;
      position: relative;
      overflow: hidden;

      .pd-webcam,
      .pd-result {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transform: scaleX(-1);
      }

      .pd-mask {
        position: absolute;
        top: 10%;
        left: 10%;
        width: 80%;
        height: 80%;
        z-index: 10;
        object-fit: contain;
      }

      .pd-anchor {
        position: absolute;
        z-index: 20;
        transform: translate(-50%, -50%);
      }

      .pd-value {
        position: absolute;
        top: 2%;
        right: 2%;
        z-index: 21;
        text-shadow: 0 0 3px #c6c6c6;
      }
    }
  }
}

.measurement-dialog {
  overflow: auto;
  white-space: nowrap;
  width: 100%;
  min-height: 140px;
  background: #ffffff;
  border: 2px solid #2f80ed;
  box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  padding: 12px 24px;
  display: flex;
  flex-direction: row;

  .pd-modal-content {
    color: red;
  }

  .center-divider {
    font-size: 18px;
    line-height: 150%;
    color: #9a9b9c;
    text-align: center;
  }

  .camera-pd {
    display: flex;
    flex-direction: column;
    align-items: center;

    .camera-pd-btn {
      border: 2px solid #f9d802;

      &:hover,
      &:focus {
        color: rgba(0, 0, 0, 0.65);
      }
    }
  }

  .manual-pd {
    .pd-submit-btn {
      border-radius: 20px;
      border: 2px solid #f9d802;

      &:hover,
      &:focus {
        color: rgba(0, 0, 0, 0.65);
      }
    }
  }

  .measurement-content {
    width: 100%;
  }
}
</style>
